import React, { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { FitRing, FitPill, FitBreakdown } from "./FitVisuals";
import SourcePanel from "./SourcePanel";
import { ArrowLeft, Loader2, Sparkles, Search, Target, ShieldCheck, RefreshCw, Bookmark, BookmarkCheck } from "lucide-react";

const Block = ({ label, accent = "#0284C7", children, testId, className = "" }) => (
  <div className={`accent-bar rounded-xl border border-slate-200 bg-white p-4 pl-5 ${className}`} style={{ "--accent": accent }} data-testid={testId}>
    <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</p>
    <div className="mt-1.5 text-sm text-slate-800">{children}</div>
  </div>
);

const Bullets = ({ items, dot = "bg-slate-300" }) => (
  <ul className="space-y-1.5">
    {(items || []).map((item, i) => (
      <li key={i} className="flex gap-2"><span className={`mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} /><span>{item}</span></li>
    ))}
  </ul>
);

const payLabel = (role) => (role.pay_min ? `₹${role.pay_min}${role.pay_max ? `-${role.pay_max}` : "+"} L` : "Not published");

const FutureRoleDetail = ({ detail, resumeId, onBack, onReload, onShortlist }) => {
  const { role, edition, groups, fit } = detail;
  const [plan, setPlan] = useState(detail.readiness || null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(!!detail.shortlisted);
  const [savingList, setSavingList] = useState(false);
  const group = (groups || []).find((g) => g.key === role.group) || { label: "", accent: "#0284C7" };

  const toggleShortlist = async () => {
    setSavingList(true);
    try {
      if (saved) {
        await api.delete(`/future-jobs/${role.id}/shortlist`);
        setSaved(false);
        toast.success("Removed from your shortlist");
      } else {
        await api.post(`/future-jobs/${role.id}/shortlist${resumeId ? `?resume_id=${resumeId}` : ""}`);
        setSaved(true);
        toast.success("Saved to your shortlist");
      }
      if (onShortlist) onShortlist();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update your shortlist");
    } finally { setSavingList(false); }
  };

  const buildPlan = async (refresh = false) => {
    if (!resumeId) { toast.error("Analyse a resume first so we can score your readiness"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/future-jobs/${role.id}/readiness?resume_id=${resumeId}${refresh ? "&refresh=true" : ""}`);
      setPlan(data.readiness);
      if (onReload) onReload();
      toast.success(data.cached ? "Your saved plan is ready" : "Your readiness plan is ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build your plan");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 rise-in" data-testid={`future-role-detail-${role.id}`}>
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-sky-700 hover:text-sky-800" data-testid="future-detail-back">
        <ArrowLeft className="h-4 w-4" /> All trending roles
      </button>

      <div className="card-soft p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-mono uppercase tracking-widest" style={{ color: group.accent }}>
              {String(role.rank).padStart(2, "0")} · {group.label}
            </p>
            <h2 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900">{role.title}</h2>
            <p className="mt-1.5 text-sm text-slate-600">{role.what_it_does}</p>
          </div>
          {fit && (
            <div className="flex items-center gap-3">
              <FitRing score={fit.score} verdict={fit.verdict} belowLevel={fit.below_level} testId={`future-detail-ring-${role.id}`} />
              <FitPill fit={fit} testId={`future-detail-pill-${role.id}`} />
            </div>
          )}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button onClick={toggleShortlist} disabled={savingList} variant={saved ? "outline" : "default"}
            className={`h-9 rounded-xl px-4 text-sm ${saved ? "border-sky-200 text-sky-700" : "bg-slate-900 text-white hover:bg-slate-800"}`}
            data-testid={`future-shortlist-toggle-${role.id}`}>
            {savingList ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : saved ? <BookmarkCheck className="mr-2 h-4 w-4" /> : <Bookmark className="mr-2 h-4 w-4" />}
            {saved ? "On your shortlist" : "Save to shortlist"}
          </Button>
          <p className="text-[11px] text-slate-500">{detail.shortlist_note}</p>
        </div>
        {fit?.below_level && (
          <p className="mt-3 rounded-xl border border-slate-300 bg-slate-50 p-3 text-xs text-slate-600" data-testid={`future-below-level-${role.id}`}>
            <strong className="font-semibold text-slate-800">Below your level.</strong> {fit.level_note}. We are not putting this
            forward as a match for you — but if you want this direction for your own reasons, it is yours to choose.
          </p>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Block label="Why it's hot" accent="#D97706" testId="future-why-hot">{role.why_hot}</Block>
        <Block label={edition?.region === "Global" ? "India pay band" : "Pay band"} accent="#0D9488" testId="future-pay">
          <span className="num-display text-base">{payLabel(role)}</span>
          <p className="mt-1 text-[11px] text-slate-500">Indicative annual pay, typical to senior levels. Real offers vary with city, employer and experience.</p>
        </Block>
        <Block label="Key skills" accent="#0284C7" testId="future-skills">
          <div className="flex flex-wrap gap-1.5">
            {(role.key_skills || []).map((s) => {
              const have = fit?.matched_skills?.includes(s);
              return <span key={s} className={`rounded-md border px-2 py-0.5 text-xs ${have ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-700"}`}>{have ? "✓ " : ""}{s}</span>;
            })}
          </div>
        </Block>
        <Block label="Titles to search" accent="#7C3AED" testId="future-titles">
          <div className="flex flex-wrap gap-1.5">
            {(role.alt_titles || []).map((t) => (
              <a key={t} href={`https://www.linkedin.com/jobs/search/?keywords=${encodeURIComponent(t)}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700 hover:border-sky-200 hover:text-sky-700">
                <Search className="h-3 w-3" /> {t}
              </a>
            ))}
          </div>
        </Block>
      </div>

      {fit && <FitBreakdown fit={fit} />}

      <div className="card-soft p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-bold text-slate-900">What it takes to get there</h3>
            <p className="mt-1 text-xs text-slate-500">A plan written against your own profile — gaps, 90 days of steps and the proof hiring managers want.</p>
          </div>
          <Button onClick={() => buildPlan(!!plan)} disabled={busy} className="h-10 rounded-xl bg-sky-600 px-5 text-white hover:bg-sky-700" data-testid={`future-readiness-button-${role.id}`}>
            {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Working…</> : plan ? <><RefreshCw className="mr-2 h-4 w-4" /> Rebuild plan</> : <><Sparkles className="mr-2 h-4 w-4" /> Build my plan</>}
          </Button>
        </div>

        {plan && (
          <div className="mt-4 space-y-3" data-testid="future-readiness-plan">
            <Block label="Honest verdict" accent="#0284C7">{plan.verdict}</Block>
            <div className="grid gap-3 sm:grid-cols-2">
              <Block label="Why you fit" accent="#059669" testId="readiness-why"><Bullets items={plan.why_you_fit} dot="bg-emerald-400" /></Block>
              <Block label="What's missing" accent="#E11D48" testId="readiness-gaps"><Bullets items={plan.gaps} dot="bg-rose-300" /></Block>
            </div>
            <Block label="Your next 90 days" accent="#D97706" testId="readiness-plan-90">
              <ol className="space-y-1.5">
                {(plan.ninety_day_plan || []).map((step, i) => (
                  <li key={i} className="flex gap-2"><span className="num-display text-xs text-amber-700">{i + 1}.</span><span>{step}</span></li>
                ))}
              </ol>
            </Block>
            <div className="grid gap-3 sm:grid-cols-2">
              <Block label="Proof to build" accent="#7C3AED" testId="readiness-proof"><Bullets items={plan.proof_to_build} /></Block>
              <Block label="Interview focus" accent="#0D9488" testId="readiness-interview"><Bullets items={plan.interview_focus} /></Block>
            </div>
            {plan.titles_to_search?.length > 0 && (
              <Block label="Search these titles" accent="#0284C7" testId="readiness-titles">
                <div className="flex flex-wrap gap-1.5">
                  {plan.titles_to_search.map((t) => (
                    <a key={t} href={`https://www.linkedin.com/jobs/search/?keywords=${encodeURIComponent(t)}`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700 hover:border-sky-200 hover:text-sky-700">
                      <Search className="h-3 w-3" /> {t}
                    </a>
                  ))}
                </div>
              </Block>
            )}
          </div>
        )}

        {!plan && !resumeId && (
          <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700" data-testid="future-readiness-needs-resume">
            Analyse or build a resume first — your plan is written against your real experience.
          </p>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Block label="What this role does" accent="#0284C7"><span className="inline-flex items-start gap-2"><Target className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />{role.what_it_does}</span></Block>
        <Block label={edition?.fit_label || "Type of work"} accent="#7C3AED"><span className="inline-flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-violet-600" />{(detail.fit_types || {})[role.fit_type] || role.fit_type}</span></Block>
      </div>

      <SourcePanel testId="future-detail-sources" items={[
        role.data_source ? `Demand data: ${role.data_source}` : "",
        edition?.pay_note,
        edition?.source_note,
      ]} />
    </div>
  );
};

export default FutureRoleDetail;
